"use client";
import * as React from 'react';
import { TESTIMONIALS } from '@/lib/constants';
import { useScrollAnimation } from '@/lib/useScrollAnimation';

export function Testimonials() {
  const { ref, isVisible } = useScrollAnimation();
  const [index, setIndex] = React.useState(0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);

  const changeTestimonial = (newIndex: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIndex(newIndex);
      setIsTransitioning(false);
    }, 300);
  };

  React.useEffect(() => {
    if (TESTIMONIALS.length === 0) return;
    const id = setInterval(() => {
      changeTestimonial((index + 1) % TESTIMONIALS.length);
    }, 7000);
    return () => clearInterval(id);
  }, [index]);

  const isEmpty = TESTIMONIALS.length === 0;
  const t = TESTIMONIALS[index];
  
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="section-padding-lg bg-brand-peach-light">
      <div className="container">
        <div className={`text-center mb-12 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            What Our Clients Say
          </h2>
          {!isEmpty ? (
            <p className="text-lg text-secondary max-w-2xl mx-auto">
              Don&apos;t just take our word for it - hear from satisfied customers
            </p>
          ) : (
            <p className="text-lg text-secondary max-w-2xl mx-auto">
              Real customer reviews will appear here once available. We&apos;re proud of the work we do and look forward to sharing our clients&apos; experiences.
            </p>
          )}
        </div>

        {!isEmpty ? (
        <div className={`max-w-4xl mx-auto ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '200ms' }}>
          <div className="rounded-2xl bg-white p-8 md:p-12 shadow-xl border border-neutral-200">
            <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
              <svg className="w-12 h-12 text-[color:var(--color-primary)] mb-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
              
              <blockquote className="text-xl md:text-2xl text-[color:var(--color-text-primary)] font-medium leading-relaxed mb-6">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[color:var(--color-primary)] to-[color:var(--color-primary-hover)] flex items-center justify-center text-white font-bold text-lg">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-lg text-primary">{t.name}</div>
                  <div className="text-sm text-muted">{t.location}</div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 flex gap-3 justify-center">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Show testimonial ${i + 1}`}
                  aria-pressed={i === index}
                  onClick={() => changeTestimonial(i)}
                  className="inline-flex min-w-[48px] min-h-[48px] items-center justify-center rounded-full hover:bg-neutral-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                >
                  <span
                    className={`h-3 w-3 rounded-full transition-all duration-300 ${
                      i === index 
                        ? 'bg-[color:var(--color-primary)] scale-125' 
                        : 'bg-neutral-300 hover:bg-neutral-400'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
        ) : (
          <div className={`text-center ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
            <p className="text-secondary">
              Reviews will be displayed here once collected from real customers.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
